import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Rock, User } from '../../types';

interface RockIssuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  rock: Rock;
  issues?: any[]; // Optional, no longer used but kept for backward compatibility
  users: User[];
  onAddIssue: (rockId: string, name: string, description: string) => Promise<void>;
  onResolveIssue?: (issueId: string, resolutionNotes?: string) => Promise<void>;
  currentUserId?: string;
}

/**
 * Rock Issues Modal Component
 * Modal for managing issues for a rock
 * Migrated from Tailwind to Chakra UI
 */
export function RockIssuesModal({
  isOpen,
  onClose,
  rock,
  users,
  onAddIssue,
  currentUserId,
}: RockIssuesModalProps) {
  const toast = useToast();

  const issueValidationSchema = Yup.object({
    name: Yup.string().required('Issue name is required').min(1, 'Issue name is required'),
    description: Yup.string().required('Description is required').min(1, 'Description is required'),
  });

  const handleAddIssue = async (values: { name: string; description: string }, { resetForm }: any) => {
    if (!currentUserId) {
      toast({
        title: 'Error',
        description: 'You must be logged in to add an issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await onAddIssue(rock.id, values.name, values.description);
      toast({
        title: 'Issue added',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      resetForm(); // Reset form
      onClose(); // Close modal after successful add
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };



  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Add Issue - {rock.rock}</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={{ name: '', description: '' }}
          validationSchema={issueValidationSchema}
          onSubmit={handleAddIssue}
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Field name="name">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Issue Name</FormLabel>
                        <Input {...field} placeholder="Enter issue name..." />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="description">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched}>
                        <FormLabel>Description</FormLabel>
                        <Textarea {...field} placeholder="Describe the issue..." rows={5} />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Add Issue
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

